import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { commifyNumber } from '../../../../helpers/common';
import { PaymentInterface } from '../../../../new-lib/types/entity';
import {
  getPlanDuration,
  isCashPayment
} from '../../../../helpers/payment-plan';
import { useRouter } from 'next/router';
import OriginalPlanIcon from '../../../../new-components/svg-icons/payment-plan/OriginalPlanIcon';
import OfferPlanIcon from '../../../../new-components/svg-icons/payment-plan/OfferPlanIcon';
import CashPaymentIcon from '../../../../new-components/svg-icons/payment-plan/CashPaymentIcon';
import NawyNowIcon from '../../../../new-components/svg-icons/payment-plan/NawyNowIcon';
import { useState } from 'react';
import Modal from '../../../../new-components/mobile/Modal';
import PropertyMortgageForm from '../../components/PropertyMortgageForm';
import { useSelector } from 'react-redux';
import animationData from '../../../entity/components/MortgageAnimatedArrow.json';
import dynamic from 'next/dynamic';

const Lottie = dynamic(
  () => import('@lottiefiles/react-lottie-player').then(mod => mod.Player),
  { ssr: false }
);

interface ChildProps {
  plan: PaymentInterface;
  isOffer?: boolean;
  entity?: any;
  isFinancing?: boolean;
}

const PropertyPaymentPlan = ({
  plan,
  isOffer,
  entity,
  isFinancing
}: ChildProps) => {
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const { t } = useTranslation(['entity', 'common']);
  const { locale } = useRouter();
  const isResalePlan = !plan?.equal_installments?.value;
  const years =
    isResalePlan && plan?.years_remaining > 0
      ? plan.years_remaining
      : plan?.years && plan?.years > 0
        ? plan.years
        : null;
  const months = isResalePlan ? undefined : plan?.months;
  const isCash = isCashPayment(
    plan.down_payment.value,
    plan.equal_installments.value,
    plan.is_cash
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const handleOpen = () => {
    setModalOpen(true);
  };
  const getPlanIcon = () => {
    if (isOffer) {
      return <OfferPlanIcon />;
    }

    if (plan.is_nawy_now) {
      return <NawyNowIcon />;
    }

    if (isCash || isResalePlan) {
      return <CashPaymentIcon />;
    }

    return <OriginalPlanIcon />;
  };

  const getPlanTitle = () => {
    return (
      <div className="plan-title">
        {getPlanIcon()}
        <span className="plan-title-text">
          {isOffer
            ? t('offer')
            : plan.is_nawy_now
              ? 'Nawy Now'
              : isCash
                ? t('cash')
                : isResalePlan
                  ? t('remaining')
                  : t('original_plan')}
        </span>
      </div>
    );
  };

  const getCardContent = () => {
    if (isOffer && plan?.cash_discount?.percent) {
      return (
        <div className="cash-discount">
          <div className="percentage-container">
            <span className="percentage">
              {Math.round(plan.cash_discount.percent)}%
            </span>
            <span className="cash-discount-text">{t('cash_discount')}</span>
          </div>
        </div>
      );
    }

    if (isCash) {
      return (
        <div className="cash-payment">
          <span className="price">{commifyNumber(plan.price)}</span>
          <span className="currency">{plan.currency}</span>
        </div>
      );
    }

    return (
      <div className="details-section">
        <div className="installments-section">
          <div className="installments">
            <span className="value">
              {isResalePlan
                ? commifyNumber(plan.price - plan.down_payment.value)
                : commifyNumber(plan.equal_installments.value)}{' '}
            </span>
            {isResalePlan ? (
              <span className="currency">{plan.currency}</span>
            ) : (
              <>
                <span className="currency">{plan.currency}</span>
                <span className="monthly">
                  {t('monthly', { ns: 'common' })}
                </span>
              </>
            )}
          </div>
          {years && (
            <span className="installment-years">
              {getPlanDuration({
                years: years,
                months: months
              })}
              {` `}
              {t('years')}
            </span>
          )}
        </div>
        <div className="down-payment">
          <span className="value">{`${commifyNumber(plan.down_payment.value)} ${plan.currency}`}</span>
          <span>{t('downpayment')}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <PropertyPaymentPlanContainer
        locale={locale}
        isOffer={isOffer}
        isCash={isCash}
        isNawyNow={plan.is_nawy_now}
        isFinancing={isFinancing}
      >
        <div className="plan-title-section">
          {getPlanTitle()}
          {plan.installment_type?.name === 'Back Loaded' && (
            <div className="plan-type">
              {t('escalating_payments', { ns: 'common' })}
            </div>
          )}
        </div>
        {getCardContent()}
        {isFinancing && isCash && (
          <div className="financing-indicator" onClick={handleOpen}>
            <span className="mortgage-text">{t('mortgage_option')}</span>{' '}
            <div className="fill-form">
              {t('fill_form')}
              <Lottie
                autoplay
                loop
                src={animationData}
                style={{
                  height: isMobile ? '28px' : '38px',
                  width: isMobile ? '28px' : '38px',
                  transform:
                    locale === 'en' ? 'rotate(-90deg)' : 'rotate(90deg)'
                }}
              />
            </div>
          </div>
        )}
        <Modal
          isOpen={modalOpen}
          closeFn={() => setModalOpen(false)}
          withCloseBtn
          modalWidth={isMobile ? 'calc(100% - 16px)' : '590px'}
          modalHeight={isMobile ? 'auto' : '575px'}
        >
          <PropertyMortgageForm entity={entity} setModalOpen={setModalOpen} />
        </Modal>
      </PropertyPaymentPlanContainer>
    </>
  );
};

const PropertyPaymentPlanContainer = styled.div<any>`
  min-width: 300px;
  max-width: 360px;
  flex: 1;
  flex-direction: column;
  border-radius: 12px;
  background: #fff;
  display: flex;
  padding: ${props =>
    props.isFinancing && props.isCash
      ? '12px 0px 0px 0px'
      : '12px 18px 11px 18px'};
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    width: 345px;
  }

  .modal-close-btn {
    background-color: transparent;
  }

  .modal-content {
    padding: 0px !important;
  }

  .financing-indicator {
    width: 100%;
    max-height: 47px;
    border-radius: 0px 0px 12px 12px;
    background: #015c9a;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;

    .mortgage-text {
      color: #fff;
      font-family: materialRegular;
      font-size: 16px;
    }

    .fill-form {
      display: flex;
      align-items: center;
      color: #fff;
      text-align: right;
      font-family: materialMedium;
      font-size: 16px;
      font-weight: 500;
    }
  }

  .plan-title-section {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: ${props =>
      props.isFinancing && props.isCash ? '0px 12px' : '0px'};
    .plan-title {
      display: flex;
      align-items: center;
      gap: 4px;
      svg {
        width: 18px;
        height: 18px;
      }

      .plan-title-text {
        color: ${props =>
          props.isOffer ? '#ff9108' : props.isNawyNow ? '#FF5E00' : '#015c9a'};
        font-family: materialMedium;
        font-size: 16px;
      }
    }
    .plan-type {
      font-family: materialMedium;
      font-size: 12px;
      color: #3e4042;
      display: flex;
      align-self: end;

      border-radius: 100px;
      background: #f2f6fd;
      padding: 4px 8px;
    }
  }

  .details-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: ${props =>
      props.isFinancing && props.isCash ? '0px 12px' : '0px'};

    .installments-section {
      height: 36px;
      display: flex;
      align-items: end;
      justify-content: space-between;
      width: 100%;
      .installments {
        display: flex;
        align-items: end;
        gap: 4px;

        .value {
          font-family: materialSemiBold;
          font-size: 14px;
          color: #2c3138;
          font-size: 24px;
        }
      }
    }
  }

  .down-payment {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;

    span {
      &:first-child {
        color: #3e4042;
        font-family: materialMedium;
      }

      &:last-child {
        color: #2c3138;
        font-family: materialRegular;
      }
    }
  }

  .installment-years {
    color: #2c3138;
    font-family: materialMedium;
    font-size: 14px;
    display: flex;
    font-family: materialSemiBold;
    margin-bottom: 3px;
  }

  .cash-payment {
    width: 100%;
    height: 100%;
    padding: ${props => (props.isFinancing ? '0px 12px' : '0px')};
    display: flex;
    gap: 4px;
    align-items: end;

    .price {
      color: #2c3138;
      font-size: 24px;
      font-family: 'materialSemiBold';
    }
  }

  .currency,
  .monthly {
    color: #2c3138;
    font-size: 14px;
    font-family: materialMedium;
    line-height: 114%;
    margin-bottom: 3px;
  }

  .cash-discount {
    .discount-details {
      color: #2c3138;
      font-family: materialRegular;
      font-size: 14px;
      margin-bottom: 4px;

      &.discount {
        width: 100%;
        color: #4b535f;
        font-family: materialRegular;
        font-size: 8px;
      }

      &.years {
        width: unset;
        font-family: materialRegular;
        font-size: 16px;
      }
    }

    .plan-duration {
      display: flex;
      color: #2c3138;
      font-family: materialMedium;
      gap: 3px;
    }

    .percentage-container {
      display: flex;
      align-items: flex-end;
      gap: 4px;

      .percentage {
        color: #2c3138;
        font-family: materialBold;
        font-size: 28px;
      }

      .cash-discount-text {
        color: #2c3138;
        font-family: materialRegular;
        font-size: 14px;
        margin-bottom: 3px;
      }

      .discount-text {
        width: 50px;
        font-family: materialRegular;
        font-size: 12px;
        align-self: flex-end;
        color: #2c3138;
        margin-bottom: 3px;
      }

      .percentage-text {
        font-size: ${props => props.theme.font.sizes.xxs};
        color: ${props => props.theme.light.colors.black};
        width: 100%;
      }
    }
  }

  @media screen and (max-width: 768px) {
    .plan-title {
      svg {
        width: 16px;
        height: 16px;
      }

      .plan-type-text {
        font-size: 12px;
      }
    }
    .down-payment {
      font-size: 12px;
      color: #2c3138;
    }
  }
`;

export default PropertyPaymentPlan;
